import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Search, Loader2, FileDown, ArrowLeft, GitCompareArrows } from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";
import { LOGO_BASE64 } from "@/lib/logo";

const HIGHLIGHT_BG = "#C2410C";

function getMargins(cost, price) {
  if (!cost || !price || cost <= 0 || price <= 0) return { mu: "—", md: "—" };
  const mu = (((price - cost) / cost) * 100).toFixed(2);
  const md = (((price - cost) / price) * 100).toFixed(2);
  return { mu, md };
}

function DesignCard({ design, selected, onToggle }) {
  const rd = design.result_data || {};
  const fd = design.form_data || {};
  const landed = (rd.landed_cost || rd.final_cost_per_dhoti || 0).toFixed(2);
  const isSelected = selected;

  return (
    <div
      data-testid={`compare-card-${design.id}`}
      onClick={onToggle}
      className={`border rounded-lg p-4 cursor-pointer transition-all ${
        isSelected
          ? "border-[#C2410C] bg-orange-50/60 ring-2 ring-[#C2410C]/30"
          : "border-stone-200 hover:border-stone-400 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-stone-900 text-sm truncate">{design.design_name}</h3>
          <p className="text-xs text-stone-500 mt-0.5 truncate">{design.description || "No description"}</p>
        </div>
        <div className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
          isSelected ? "border-[#C2410C] bg-[#C2410C]" : "border-stone-300"
        }`}>
          {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div>
          <span className="text-stone-500 block">Landed</span>
          <span className="font-mono font-semibold text-[#C2410C]">{landed}</span>
        </div>
        <div>
          <span className="text-stone-500 block">WSP</span>
          <span className="font-mono font-medium">{rd.wsp_price || "—"}</span>
        </div>
        <div>
          <span className="text-stone-500 block">MRP</span>
          <span className="font-mono font-medium">{rd.mrp_price || "—"}</span>
        </div>
      </div>
      <div className="mt-2 text-[11px] text-stone-400">
        {fd.warp_count}/{fd.warp_quality} | {fd.border_type} | {fd.reed}R {fd.pick}P
      </div>
    </div>
  );
}

function ComparisonColumn({ design, label }) {
  if (!design) return null;
  const rd = design.result_data || {};
  const fd = design.form_data || {};
  const s = parseFloat(fd.size) || 1;
  const landed = (rd.landed_cost || rd.final_cost_per_dhoti || 0).toFixed(2);
  const wspM = rd.wsp_price && rd.landed_cost ? getMargins(rd.landed_cost, rd.wsp_price) : null;
  const mrpM = rd.mrp_price && rd.wsp_price ? getMargins(rd.wsp_price, rd.mrp_price) : null;

  const highlightRow = "bg-[#C2410C] text-white font-bold rounded px-2";
  const normalRow = "";

  return (
    <div className="flex-1 min-w-0">
      <div className="text-center mb-3">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-400">{label}</span>
        <h3 className="font-heading text-base font-bold text-stone-900 truncate mt-1">{design.design_name}</h3>
      </div>

      {/* Specs */}
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 mb-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-2">Specifications</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <span className="text-stone-500">Fabric Width</span>
          <span className="text-right font-mono">{fd.fabric_width}</span>
          <span className="text-stone-500">Border Width</span>
          <span className="text-right font-mono">{fd.border_width}</span>
          <span className="text-stone-500">Loom Reed</span>
          <span className="text-right font-mono">{fd.reed}</span>
          <span className="text-stone-500">Loom Pick</span>
          <span className="text-right font-mono">{fd.pick}</span>
          <span className="text-stone-500">Fabric Reed</span>
          <span className="text-right font-mono">{parseFloat(fd.reed) + 4}</span>
          <span className="text-stone-500">Fabric Pick</span>
          <span className="text-right font-mono">{parseFloat(fd.pick) - 2}</span>
          <span className="text-stone-500">Pick Rate</span>
          <span className="text-right font-mono">{fd.pick_rate}</span>
          <span className="text-stone-500">Size</span>
          <span className="text-right font-mono">{fd.size}m</span>
          <span className="text-stone-500">Border</span>
          <span className="text-right">{fd.border_type}</span>
          <span className="text-stone-500">Bobbin</span>
          <span className="text-right capitalize">{fd.bobbin_type}</span>
        </div>
      </div>

      {/* Yarn */}
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 mb-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-2">Yarn</p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <span className="text-stone-500">Warp</span>
          <span className="text-right">{rd.warp_count}/{rd.warp_quality} <span className="text-stone-400 text-[10px]">@ {rd.warp_price_per_unit ?? "—"}</span></span>
          <span className="text-stone-500">Weft</span>
          <span className="text-right">{rd.weft_count}/{rd.weft_quality} <span className="text-stone-400 text-[10px]">@ {rd.weft_price_per_unit ?? "—"}</span></span>
          <span className="text-stone-500">Kuri</span>
          <span className="text-right">{rd.kuri_count}/{rd.kuri_quality} <span className="text-stone-400 text-[10px]">@ {rd.kuri_price_per_unit ?? "—"}</span></span>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="bg-white border border-stone-200 rounded-lg p-3 mb-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-2">Cost Breakdown</p>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between"><span className="text-stone-500">Warp Total</span><span className="font-mono">{rd.warp_total_cost?.toFixed(2) || "—"}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Weft</span><span className="font-mono">{rd.weft_cost?.toFixed(2) || "—"}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Bobbin</span><span className="font-mono">{rd.total_bobbin_cost?.toFixed(2) || "—"}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Kuri</span><span className="font-mono">{rd.kuri_cost?.toFixed(2) || "—"}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Weaving</span><span className="font-mono">{rd.weaving_wages?.toFixed(2) || "—"}</span></div>
          <Separator className="my-1" />
          <div className="flex justify-between font-semibold"><span>Cost/Dhoti</span><span className="font-mono">{rd.cost_per_dhoti?.toFixed(2) || "—"}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Weaver ({rd.weaver_profit_pct || 10}%)</span><span className="font-mono">{rd.profit_amount?.toFixed(2) || "—"}</span></div>
          <div className={`flex justify-between py-1 ${highlightRow}`}>
            <span>Greige Cost</span><span className="font-mono">{rd.greige_cost_per_dhoti?.toFixed(2) || "—"}</span>
          </div>
        </div>
      </div>

      {/* Post Production */}
      <div className="bg-white border border-stone-200 rounded-lg p-3 mb-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-2">Post Production</p>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between"><span className="text-stone-500">Processing</span><span className="font-mono">{rd.processing_cost?.toFixed(2) || "—"}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Folding</span><span className="font-mono">{rd.folding_cost?.toFixed(2) || "—"}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Trims</span><span className="font-mono">{rd.trims_cost?.toFixed(2) || "—"}</span></div>
          <div className="flex justify-between"><span className="text-stone-500">Warehouse ({rd.warehouse_pct || 5}%)</span><span className="font-mono">{rd.warehouse_cost?.toFixed(2) || "—"}</span></div>
          <Separator className="my-1" />
          <div className={`flex justify-between py-1 ${highlightRow}`}>
            <span>Landed Cost</span><span className="font-mono">{landed}</span>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="bg-white border border-stone-200 rounded-lg p-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-2">Pricing</p>
        <div className="space-y-1 text-xs">
          {rd.wsp_price ? (
            <>
              <div className={`flex justify-between py-1 ${highlightRow}`}>
                <span>WSP</span><span className="font-mono">{rd.wsp_price}</span>
              </div>
              {wspM && (
                <>
                  <div className={`flex justify-between py-1 ${highlightRow}`}>
                    <span>WSP Markup</span><span className="font-mono">{wspM.mu}%</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-stone-500">WSP Markdown</span><span className="font-mono">{wspM.md}%</span>
                  </div>
                </>
              )}
            </>
          ) : <div className="text-stone-400">No WSP set</div>}
          {rd.mrp_price ? (
            <>
              <div className={`flex justify-between py-1 ${highlightRow}`}>
                <span>MRP</span><span className="font-mono">{rd.mrp_price}</span>
              </div>
              {mrpM && (
                <>
                  <div className="flex justify-between py-1">
                    <span className="text-stone-500">MRP Markup</span><span className="font-mono">{mrpM.mu}%</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-stone-500">MRP Markdown</span><span className="font-mono">{mrpM.md}%</span>
                  </div>
                </>
              )}
            </>
          ) : <div className="text-stone-400">No MRP set</div>}
        </div>
      </div>
    </div>
  );
}

export default function CompareDesignsPage() {
  const [designs, setDesigns] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparing, setComparing] = useState(false);

  const fetchDesigns = useCallback(async () => {
    try {
      const res = await api.get("/saved-designs");
      setDesigns(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDesigns(); }, [fetchDesigns]);

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) {
        toast.info("You can only select 2 designs to compare");
        return prev;
      }
      return [...prev, id];
    });
  };

  const selectedDesigns = designs.filter((d) => selectedIds.includes(d.id));

  const filtered = designs.filter((d) => {
    const q = search.toLowerCase();
    return (
      d.design_name?.toLowerCase().includes(q) ||
      d.description?.toLowerCase().includes(q) ||
      d.form_data?.border_type?.toLowerCase().includes(q)
    );
  });

  const exportComparisonPdf = () => {
    if (selectedDesigns.length !== 2) return;
    const [d1, d2] = selectedDesigns;

    const buildCol = (d) => {
      const rd = d.result_data || {};
      const fd = d.form_data || {};
      const s = parseFloat(fd.size) || 1;
      const landed = (rd.landed_cost || rd.final_cost_per_dhoti || 0).toFixed(2);
      const wspM = rd.wsp_price && rd.landed_cost ? getMargins(rd.landed_cost, rd.wsp_price) : null;
      const mrpM = rd.mrp_price && rd.wsp_price ? getMargins(rd.wsp_price, rd.mrp_price) : null;

      const row = (label, val, highlight) =>
        highlight
          ? `<tr style="background:${HIGHLIGHT_BG};"><td style="padding:7px 8px;color:white;font-weight:700;font-size:13px;">${label}</td><td style="padding:7px 8px;text-align:right;font-family:monospace;color:white;font-weight:700;font-size:13px;">${val}</td></tr>`
          : `<tr><td style="padding:5px 2px;color:#57534E;">${label}</td><td style="padding:5px 2px;text-align:right;font-family:monospace;">${val}</td></tr>`;

      return `
        <div style="flex:1;min-width:0;padding:0 6px;">
          <div style="text-align:center;margin-bottom:8px;">
            <div style="font-size:16px;font-weight:700;color:#1C1917;">${d.design_name}</div>
          </div>
          <div style="background:#F7F6F2;border:1px solid #E7E5E4;border-radius:6px;padding:8px 10px;margin-bottom:6px;">
            <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:5px;">Specifications</div>
            <div style="font-size:13px;display:flex;flex-wrap:wrap;gap:3px 12px;">
              <div><span style="color:#78716c;">FW:</span> <b>${fd.fabric_width}</b></div>
              <div><span style="color:#78716c;">BW:</span> <b>${fd.border_width}</b></div>
              <div><span style="color:#78716c;">L.Reed:</span> <b>${fd.reed}</b></div>
              <div><span style="color:#78716c;">L.Pick:</span> <b>${fd.pick}</b></div>
              <div><span style="color:#78716c;">F.Reed:</span> <b>${parseFloat(fd.reed) + 4}</b></div>
              <div><span style="color:#78716c;">F.Pick:</span> <b>${parseFloat(fd.pick) - 2}</b></div>
              <div><span style="color:#78716c;">Pick Rate:</span> <b>${fd.pick_rate}</b></div>
              <div><span style="color:#78716c;">Size:</span> <b>${fd.size}m</b></div>
              <div><span style="color:#78716c;">Border:</span> <b>${fd.border_type}</b></div>
            </div>
            <div style="font-size:13px;display:flex;flex-wrap:wrap;gap:3px 12px;margin-top:4px;">
              <div><span style="color:#78716c;">Warp:</span> <b>${rd.warp_count}/${rd.warp_quality}</b> <span style="color:#a8a29e;font-size:10px;">@ ${rd.warp_price_per_unit ?? "—"}</span></div>
              <div><span style="color:#78716c;">Weft:</span> <b>${rd.weft_count}/${rd.weft_quality}</b> <span style="color:#a8a29e;font-size:10px;">@ ${rd.weft_price_per_unit ?? "—"}</span></div>
              <div><span style="color:#78716c;">Kuri:</span> <b>${rd.kuri_count}/${rd.kuri_quality}</b> <span style="color:#a8a29e;font-size:10px;">@ ${rd.kuri_price_per_unit ?? "—"}</span></div>
            </div>
          </div>
          <div style="background:white;border:1px solid #E7E5E4;border-radius:6px;padding:8px 10px;margin-bottom:6px;">
            <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:5px;">Cost Breakdown</div>
            <table style="width:100%;border-collapse:collapse;font-size:13px;">
              ${row("Warp Total", rd.warp_total_cost?.toFixed(2) || "—")}
              ${row("Weft", rd.weft_cost?.toFixed(2) || "—")}
              ${row("Bobbin", rd.total_bobbin_cost?.toFixed(2) || "—")}
              ${row("Kuri", rd.kuri_cost?.toFixed(2) || "—")}
              ${row("Weaving", rd.weaving_wages?.toFixed(2) || "—")}
              <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:1px 0;"></td></tr>
              ${row("Cost/Dhoti", rd.cost_per_dhoti?.toFixed(2) || "—")}
              ${row(`Weaver (${rd.weaver_profit_pct || 10}%)`, rd.profit_amount?.toFixed(2) || "—")}
              ${row("Greige Cost", rd.greige_cost_per_dhoti?.toFixed(2) || "—", true)}
            </table>
          </div>
          <div style="background:white;border:1px solid #E7E5E4;border-radius:6px;padding:8px 10px;margin-bottom:6px;">
            <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:5px;">Post Production</div>
            <table style="width:100%;border-collapse:collapse;font-size:13px;">
              ${row("Processing", rd.processing_cost?.toFixed(2) || "—")}
              ${row("Folding", rd.folding_cost?.toFixed(2) || "—")}
              ${row("Trims", rd.trims_cost?.toFixed(2) || "—")}
              ${row(`Warehouse (${rd.warehouse_pct || 5}%)`, rd.warehouse_cost?.toFixed(2) || "—")}
              <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:1px 0;"></td></tr>
              ${row("Landed Cost", landed, true)}
            </table>
          </div>
          <div style="background:white;border:1px solid #E7E5E4;border-radius:6px;padding:8px 10px;">
            <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:5px;">Pricing</div>
            <table style="width:100%;border-collapse:collapse;font-size:13px;">
              ${rd.wsp_price ? row("WSP", rd.wsp_price, true) : '<tr><td style="color:#a8a29e;padding:5px 2px;">No WSP</td></tr>'}
              ${wspM ? row("WSP Markup", wspM.mu + "%", true) : ""}
              ${wspM ? row("WSP Markdown", wspM.md + "%") : ""}
              ${rd.mrp_price ? row("MRP", rd.mrp_price, true) : '<tr><td style="color:#a8a29e;padding:5px 2px;">No MRP</td></tr>'}
              ${mrpM ? row("MRP Markup", mrpM.mu + "%") : ""}
              ${mrpM ? row("MRP Markdown", mrpM.md + "%") : ""}
            </table>
          </div>
        </div>`;
    };

    const html = `
    <div style="font-family:'Work Sans','IBM Plex Sans',sans-serif;max-width:700px;font-size:13px;color:#1C1917;">
      <div style="text-align:center;margin-bottom:10px;padding:6px 0;background:white;">
        <img src="${LOGO_BASE64}" style="height:42px;margin:0 auto;" />
        <div style="font-size:14px;color:#78716c;margin-top:5px;letter-spacing:2px;text-transform:uppercase;font-weight:600;">Design Comparison</div>
      </div>
      <div style="display:flex;gap:8px;">
        ${buildCol(d1)}
        <div style="width:1px;background:#E7E5E4;flex-shrink:0;"></div>
        ${buildCol(d2)}
      </div>
      <div style="display:flex;gap:16px;margin-top:14px;">
        <div style="flex:1;border:1px solid #E7E5E4;border-radius:6px;padding:10px 14px;">
          <div style="font-size:9px;color:#78716c;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">New WSP</div>
          <div style="height:22px;border-bottom:1px solid #ccc;"></div>
        </div>
        <div style="flex:1;border:1px solid #E7E5E4;border-radius:6px;padding:10px 14px;">
          <div style="font-size:9px;color:#78716c;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">New MRP</div>
          <div style="height:22px;border-bottom:1px solid #ccc;"></div>
        </div>
      </div>
      <div style="text-align:center;font-size:11px;color:#a8a29e;margin-top:10px;">Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
    </div>`;

    const container = document.createElement("div");
    container.innerHTML = html;
    document.body.appendChild(container);
    html2pdf().set({
      margin: [6, 6, 6, 6],
      filename: `Comparison_${d1.design_name}_vs_${d2.design_name}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.92 },
      html2canvas: { scale: 1.5, useCORS: true, logging: false },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    }).from(container).save().then(() => {
      document.body.removeChild(container);
    });
    toast.success("Comparison PDF exported!");
  };

  // Comparison view
  if (comparing && selectedDesigns.length === 2) {
    return (
      <div className="space-y-6" data-testid="compare-designs-view">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              data-testid="compare-back-button"
              className="h-8 w-8"
              onClick={() => setComparing(false)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="font-heading text-xl sm:text-2xl font-semibold text-stone-900 tracking-tight">
                Design Comparison
              </h1>
              <p className="text-xs text-stone-500 mt-0.5">
                {selectedDesigns[0].design_name} vs {selectedDesigns[1].design_name}
              </p>
            </div>
          </div>
          <Button
            data-testid="compare-export-pdf-button"
            className="bg-[#C2410C] hover:bg-[#9A3412] text-white"
            onClick={exportComparisonPdf}
          >
            <FileDown className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
        <div className="flex gap-6">
          <ComparisonColumn design={selectedDesigns[0]} label="Design A" />
          <div className="w-px bg-stone-200 shrink-0" />
          <ComparisonColumn design={selectedDesigns[1]} label="Design B" />
        </div>
      </div>
    );
  }

  // Selection view
  return (
    <div className="space-y-6" data-testid="compare-designs-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
          Compare Designs
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          Select 2 saved designs to compare them side by side.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            data-testid="compare-search-input"
            placeholder="Search designs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-11"
          />
        </div>
        <Button
          data-testid="compare-start-button"
          disabled={selectedIds.length !== 2}
          className="bg-[#C2410C] hover:bg-[#9A3412] text-white disabled:opacity-40"
          onClick={() => setComparing(true)}
        >
          <GitCompareArrows className="h-4 w-4 mr-2" />
          Compare ({selectedIds.length}/2)
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-stone-400">
          {designs.length === 0
            ? "No saved designs yet. Save designs from the calculator first."
            : "No matching designs found."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((d) => (
            <DesignCard
              key={d.id}
              design={d}
              selected={selectedIds.includes(d.id)}
              onToggle={() => toggleSelection(d.id)}
            />
          ))}
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-stone-900 text-white rounded-full px-5 py-2.5 shadow-lg flex items-center gap-3 text-sm">
          <span>{selectedIds.length} of 2 selected</span>
          {selectedIds.length === 2 && (
            <Button
              size="sm"
              data-testid="compare-floating-button"
              className="bg-[#C2410C] hover:bg-[#9A3412] text-white h-7 px-3 text-xs rounded-full"
              onClick={() => setComparing(true)}
            >
              Compare Now
            </Button>
          )}
          <button
            className="text-stone-400 hover:text-white text-xs underline"
            onClick={() => setSelectedIds([])}
            data-testid="compare-clear-button"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
